import { useMemo, useState } from 'react';
import { useAppSelector, useTranslate } from '@src/core/hooks';
import {
  adderResumeSelector,
  currentProductAdderStatus,
  loadingAnyTariffVerificationsSelector,
} from '@src/stores/product';
import useScheduleTimeSelection from '@src/domains/products/hooks/useScheduleTimeSelection';
import type { TimeSelectorContentCopy, TimeSelectorPopoverCopy, UseTimeSelectorResult } from './types';
import { useWidgetId } from '@src/core/IdProvider';
import { useGuardedSequentialOpenChange } from '../../hooks/useGuardedSequentialOpenChange';
import { trackStepConfirmed } from '@src/domains/products/features/product-adder-v2/analytics/widgetTracking';

export function useTimeSelector(): UseTimeSelectorResult {
  // Sold-out times stay visible (disabled) so the shopper knows the experience runs then.
  const selection = useScheduleTimeSelection({ includeSoldOutHours: true });
  const loadingTariffVerifications = useAppSelector(loadingAnyTariffVerificationsSelector);
  const { i18n } = useTranslate();

  const widgetId = useWidgetId();
  const { productId } = useAppSelector(adderResumeSelector(widgetId));
  const currentStatus = useAppSelector(currentProductAdderStatus(widgetId));

  const isAddingToCart = useMemo(() => currentStatus === 'adding_to_cart', [currentStatus]);

  const [open, setOpen] = useState(false);

  const guardedOnOpenChange = useGuardedSequentialOpenChange('time', setOpen);

  const contentCopy: TimeSelectorContentCopy = {
    unavailableMessage: i18n('adder_hours_unavailable_error'),
    daySectionTitle: i18n('adder_day'),
    nightSectionTitle: i18n('adder_night'),
  };

  const popoverCopy: TimeSelectorPopoverCopy = {
    placeholder: i18n('adder_hour'),
    bottomSheetTitle: i18n('adder_hour'),
    confirmLabel: i18n('base_confirm'),
  };

  const handleConfirm = () => {
    trackStepConfirmed(widgetId, productId ?? '', 'time');
    setOpen(false);
  };

  return {
    popover: {
      ...popoverCopy,
      disabled: loadingTariffVerifications || isAddingToCart,
      open,
      onOpenChange: guardedOnOpenChange,
      selectedFormattedTime: selection.selectedHour?.formattedTime ?? null,
      onConfirm: handleConfirm,
    },
    content: {
      ...contentCopy,
      showUnavailableMessage: selection.areAllTariffsHoursUnavailable,
      morningHours: selection.morningHours,
      lateHours: selection.lateHours,
      hasOnlyOneShift: selection.hasOnlyOneShift,
      selectedMinutes: selection.selectedHour?.minutes,
      onSelectHour: selection.onSelectHour,
    },
  };
}
